import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Save, Mail, Search, Bell, ShoppingBag, Package, User, CheckCircle, XCircle, AlertTriangle, Edit2, X } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface MessageSceneConfig {
  id: string;
  scene_type: string;
  scene_name: string;
  category: string;
  title_template: string;
  content_template: string;
  variables?: any;
  is_enabled: boolean;
  priority: string;
}

const CATEGORY_MAP: Record<string, { label: string; icon: any; color: string }> = {
  system: { label: '系统通知', icon: Bell, color: 'blue' },
  order: { label: '订单相关', icon: ShoppingBag, color: 'green' },
  package: { label: '套餐相关', icon: Package, color: 'purple' },
  teacher: { label: '教师相关', icon: User, color: 'orange' },
};

const PRIORITY_MAP: Record<string, { label: string; color: string; icon: any }> = {
  high: { label: '高优先级', color: 'red', icon: AlertTriangle },
  normal: { label: '普通', color: 'blue', icon: Bell },
  low: { label: '低优先级', color: 'gray', icon: Bell },
};

export default function AdminMessageConfig() {
  const [configs, setConfigs] = useState<MessageSceneConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingConfig, setEditingConfig] = useState<MessageSceneConfig | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('message_scene_config')
      .select('*')
      .order('category', { ascending: true });
    if (data) setConfigs(data);
    setLoading(false);
  };

  const toggleEnabled = async (config: MessageSceneConfig) => {
    setSaving(config.id);
    await supabase
      .from('message_scene_config')
      .update({ is_enabled: !config.is_enabled })
      .eq('id', config.id);
    await loadConfigs();
    setSaving(null);
  };

  const updatePriority = async (config: MessageSceneConfig, priority: string) => {
    setSaving(config.id);
    await supabase
      .from('message_scene_config')
      .update({ priority })
      .eq('id', config.id);
    await loadConfigs();
    setSaving(null);
  };

  const saveEdit = async () => {
    if (!editingConfig) return;
    setSaving(editingConfig.id);
    await supabase
      .from('message_scene_config')
      .update({
        title_template: editingConfig.title_template,
        content_template: editingConfig.content_template,
      })
      .eq('id', editingConfig.id);
    await loadConfigs();
    setSaving(null);
    setEditingConfig(null);
  };

  const filteredConfigs = configs.filter(c => {
    const matchesSearch = c.scene_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        c.scene_type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || c.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const groupedConfigs = filteredConfigs.reduce((acc, config) => {
    if (!acc[config.category]) acc[config.category] = [];
    acc[config.category].push(config);
    return acc;
  }, {} as Record<string, MessageSceneConfig[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">自动消息场景配置</h1>
          <p className="text-sm text-gray-500 mt-1">配置12个业务场景的自动站内信触发规则</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="搜索场景名称..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              categoryFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            全部
          </button>
          {Object.entries(CATEGORY_MAP).map(([key, { label, icon: Icon, color }]) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                categoryFilter === key ? `bg-${color}-600 text-white` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedConfigs).map(([category, items]) => {
          const catInfo = CATEGORY_MAP[category] || { label: category, icon: Bell, color: 'gray' };
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className={`px-6 py-4 bg-${catInfo.color}-50 border-b border-${catInfo.color}-100 flex items-center gap-3`}>
                <catInfo.icon className={`w-5 h-5 text-${catInfo.color}-600`} />
                <h2 className={`font-semibold text-${catInfo.color}-900`}>{catInfo.label}</h2>
                <span className={`ml-auto px-2 py-0.5 bg-${catInfo.color}-100 text-${catInfo.color}-700 text-xs rounded-full`}>
                  {items.length} 个场景
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {items.map((config) => {
                  const priorityInfo = PRIORITY_MAP[config.priority] || PRIORITY_MAP.normal;
                  return (
                    <div key={config.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-${catInfo.color}-100 flex items-center justify-center`}>
                            <Mail className={`w-5 h-5 text-${catInfo.color}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{config.scene_name}</h3>
                            <p className="text-xs text-gray-500">{config.scene_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={config.priority}
                            onChange={(e) => updatePriority(config, e.target.value)}
                            disabled={saving === config.id}
                            className={`px-2 py-1 text-xs rounded border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          >
                            {Object.entries(PRIORITY_MAP).map(([key, { label }]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => toggleEnabled(config)}
                            disabled={saving === config.id}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              config.is_enabled
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {saving === config.id ? '保存中...' : config.is_enabled ? '已启用' : '已禁用'}
                          </button>
                          <button
                            onClick={() => setEditingConfig(config)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">标题：</span>
                          <span className="text-sm text-gray-800">{config.title_template}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">内容：</span>
                          <span className="text-sm text-gray-800 line-clamp-2">{config.content_template}</span>
                        </div>
                        {config.variables.length > 0 && (
                          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                            <span className="text-xs text-gray-500">变量：</span>
                            <div className="flex gap-1">
                              {config.variables.map((v: string) => (
                                <span key={v} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  {'{{' + v + '}}'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {editingConfig && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">编辑模板</h2>
                <button
                  onClick={() => setEditingConfig(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto max-h-[50vh]">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">标题模板</label>
                  <input
                    type="text"
                    value={editingConfig.title_template}
                    onChange={(e) => setEditingConfig({ ...editingConfig, title_template: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">内容模板</label>
                  <textarea
                    value={editingConfig.content_template}
                    onChange={(e) => setEditingConfig({ ...editingConfig, content_template: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
                {editingConfig.variables.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-900 mb-2">可用变量</p>
                    <div className="flex flex-wrap gap-2">
                      {editingConfig.variables.map((v: string) => (
                        <span key={v} className="px-2 py-1 bg-white text-blue-700 text-sm rounded border border-blue-200">
                          {'{{' + v + '}}'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                <button
                  onClick={() => setEditingConfig(null)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving === editingConfig.id}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving === editingConfig.id && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  保存
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
